/// <reference path="../../../types/sdk-extensions.d.ts" />
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { catchError, filter, firstValueFrom, map, Observable } from "rxjs";
import {
  Inmate,
  InmatesService,
  InstanceConfig,
  InstanceService,
  SearchInmates200Response
} from '@tylertech/cj-inmate-search-angular-sdk';
import { DialogService, IDialogOptions } from '@tylertech/forge-angular';
import { PublicSearchSortFilterComponent } from './public-search-sort-filter/public-search-sort-filter.component';
import { FilterOrSearchModel, LastNDays } from 'src/app/models/filter-or-search.model';
import { InmateDetailsComponent } from './inmate-details/inmate-details.component';
import { DocInmateDetailsComponent } from './doc-inmate-details/doc-inmate-details.component';
import { AdvancedSearchModalComponent } from '../../components/landing-page/advanced-search-modal/advanced-search-modal.component';
import { ActivatedRoute, ParamMap, Router } from "@angular/router";
import { ErrorPageService } from "../../modules/shared/services/error-page.service";
import { getStateFromLoadError } from "../../modules/shared/utils/error.utils";
import { SearchInmates200ResponsePagination } from "@tylertech/cj-inmate-search-angular-sdk/model/searchInmates200ResponsePagination";
import { Subscription } from "rxjs/internal/Subscription";
import { BreakpointObserver } from '@angular/cdk/layout';
import { SearchFilterChipService } from 'src/app/core/services/search-filter-chip.service';
import { InstanceConfigService } from 'src/app/core/services/instance-config.service';
import { AdvancedSearchStateService } from 'src/app/core/services/advanced-search-state.service';
import { getDynamicOptionLabel } from 'src/app/constants/doc-options.constants';
import {
  AdvancedSearchOptionLabels,
  AdvancedSearchOptionsService
} from 'src/app/core/services/advanced-search-options.service';
import { DateUtils } from 'src/app/utils/date.utils';
import { isSearchDisabled } from '../../utils/search.utils';
import { IconRegistry } from '@tylertech/forge';
import { tylIconWarning } from '@tylertech/tyler-icons/standard';
import { tylIconAlert } from '@tylertech/tyler-icons/extended';

IconRegistry.define([tylIconWarning, tylIconAlert]);

@Component({
  selector: 'public-search',
  templateUrl: './public-search.component.html',
  styleUrls: ['./public-search.component.scss']
})

export class PublicSearchComponent implements AfterViewInit, OnInit, OnDestroy {
  @Output() personSelected = new EventEmitter<Inmate>();
  @ViewChild('searchBox') searchBox!: ElementRef;
  @ViewChildren('chipElement')
    chipElements!: QueryList<ElementRef>;
  @ViewChild('chipset') chipset: any;

  private instanceAlias: string | null = null;
  private instanceConfig: InstanceConfig | null = null;

  get showAdvancedSearch(): boolean {
    if (this.instanceConfig?.branding?.searchResultType !== 'doc') return false;
    return this.instanceConfig?.inmateVisibility?.enableAdvancedSearch ?? false;
  }
  public inmates: Inmate[] = [];
  public pagination: SearchInmates200ResponsePagination | null = null;
  public searchModel: FilterOrSearchModel = {
    nameOrId: null, sortBy: 'recentArrestDate', arrestingAgencies: [],
    minAge: null, maxAge: null, minArrestDate: null, maxArrestDate: null, dateRange: null
  };
  public loading = true;
  private readonly _subscriptions: Subscription[] = [];
  public isDocSearch = false;
  public docPageNumber = 1;

  // Single source of truth for DOC "Sort by" options. Each entry carries both
  // the dropdown label and the API sort mapping (`apiSortBy`/`apiSort`), so
  // getSortParams() derives its result from this list instead of a parallel
  // switch. Adding an option here is a compile error until its API mapping is
  // filled in, which prevents the dropdown and the API mapping from drifting.
  readonly docSortOptions: { value: string; label: string; apiSortBy: 'age' | 'arrestDate'; apiSort: 'asc' | 'desc' }[] = [
    { value: 'inmateName', label: 'Inmate Name', apiSortBy: 'name' as 'age' | 'arrestDate', apiSort: 'asc' },
    { value: 'offenderDoc', label: 'Offender DOC', apiSortBy: 'docNumber' as 'age' | 'arrestDate', apiSort: 'asc' },
    { value: 'offenderPid', label: 'Offender PID', apiSortBy: 'pid' as 'age' | 'arrestDate', apiSort: 'asc' },
    { value: 'location', label: 'Location', apiSortBy: 'location' as 'age' | 'arrestDate', apiSort: 'asc' }
  ];

  // Precomputed once from docSortOptions (which never changes after
  // construction) so the docSortBy getter's template binding doesn't
  // re-allocate this on every change detection cycle.
  private readonly docSortValues: ReadonlySet<string> = new Set(
    this.docSortOptions.map(option => option.value)
  );

  public searchParamChips$!: Observable<{ key: keyof FilterOrSearchModel, value: string, orderAdded: number }[]>;

  public isMobile = false;
  public allChipsShowingMobile = false;
  public hiddenChips = 0;
  protected updatedAt$: Observable<string | null> | undefined;
  private _queryParamsInitialized = false;

  private _optionLabels: AdvancedSearchOptionLabels = {};
  private _optionLabelsInstanceId: string | null = null;
  private _lastRenderedInstanceId: string | null = null;
  private _searchSubscription: Subscription | null = null;

  constructor(
    private readonly _errorPageService: ErrorPageService,
    private readonly _inmateService: InmatesService,
    private readonly _dialogService: DialogService,
    private readonly _router: Router,
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _breakpointObserver: BreakpointObserver,
    private readonly _searchFilterChipService: SearchFilterChipService,
    private readonly _instanceConfigService: InstanceConfigService,
    private readonly _instanceService: InstanceService,
    private readonly _advancedSearchStateService: AdvancedSearchStateService,
    private readonly _advancedSearchOptionsService: AdvancedSearchOptionsService
  ) {
    // Subscribe to config changes
    this._subscriptions.push(
      this._instanceConfigService.config$.subscribe(config => {
        const newInstanceId = config?.instanceId ?? null;
        this.instanceConfig = config;
        this._advancedSearchStateService.setInstanceId(newInstanceId);
        const isDocConfig = config?.branding?.searchResultType === 'doc';
        this.loadOptionLabels(isDocConfig ? newInstanceId ?? undefined : undefined);

        if (!newInstanceId) {
          if (this._searchSubscription) {
            this._searchSubscription.unsubscribe();
            this._searchSubscription = null;
          }
          this.inmates = [];
          this.pagination = null;
          this.docPageNumber = 1;
          this.loading = false;
          this._lastRenderedInstanceId = null;
          return;
        }

        const instanceChanged = this._lastRenderedInstanceId !== newInstanceId;

        if (instanceChanged) {
          if (this._searchSubscription) {
            this._searchSubscription.unsubscribe();
            this._searchSubscription = null;
          }
          this.inmates = [];
          this.pagination = null;
          this.docPageNumber = 1;
          this.loading = true;
          if (this._queryParamsInitialized) {
            this.loadInmates();
          }
        } else if (this.loading && this._queryParamsInitialized) {
          this.loadInmates();
        }
      })
    );
  }

  private loadOptionLabels(instanceId: string | undefined): void {
    if (!instanceId) {
      this._optionLabels = {};
      this._optionLabelsInstanceId = null;
      return;
    }

    if (instanceId === this._optionLabelsInstanceId) {
      return;
    }

    // Prevent cross-instance label bleed while the new request is in-flight.
    this._optionLabels = {};
    this._optionLabelsInstanceId = instanceId;

    this._subscriptions.push(
      this._advancedSearchOptionsService.getOptionLabels(instanceId)
        .subscribe(labels => {
          if (this._optionLabelsInstanceId === instanceId) {
            this._optionLabels = labels;
          }
        })
    );
  }

  ngOnDestroy = () => this._subscriptions.forEach(sub => sub.unsubscribe());

  ngOnInit(): void {
    this.instanceAlias = this._activatedRoute.snapshot.paramMap.get('alias') ??
      this._activatedRoute.parent?.snapshot.paramMap.get('alias') ??
      null;

    if (this.instanceAlias == null) {
      this._errorPageService.showErrorPage(ErrorPageService.NotFound);
    }

    this.searchParamChips$ = this._searchFilterChipService.$searchParamChips;
  }

  onInput(): void {
    // This method can be used to add additional logic on input if needed
  }

  async ngAfterViewInit() {
    try {
      await firstValueFrom(this._instanceConfigService.config$.pipe(
        filter(config => config !== null)
      ));
    } catch (err) {
      console.error(err);
      const state = getStateFromLoadError(err);
      this._errorPageService.showErrorPage(state);
      return;
    }

    this._subscriptions.push(
      this._activatedRoute.queryParamMap
        .pipe(
          map((params: ParamMap) => ({
            nameOrId: params.get('nameOrId') ? params.get('nameOrId')?.trim() : null,
            sortBy: params.get('sortBy') ?? 'recentArrestDate',
            arrestingAgencies: params.getAll('arrestingAgencies'),
            minAge: params.get('minAge') ? Number(params.get('minAge')) : null,
            maxAge: params.get('maxAge') ? Number(params.get('maxAge')) : null,
            minArrestDate: params.get('minArrestDate'),
            maxArrestDate: params.get('maxArrestDate'),
            dateRange: params.get('dateRange'),
            offenderType: params.get('offenderType') || params.get('offender-type'),
            docNumber: params.get('docNumber') || params.get('doc-number'),
            pid: params.get('pid'),
            supervisionStatus: params.get('supervisionStatus') || params.get('supervision-status'),
            locationType: params.get('locationType') || params.get('location-type'),
            facility: params.get('facility'),
            countyOfSupervision: params.get('countyOfSupervision') || params.get('county-of-supervision'),
            gender: params.get('gender'),
            race: params.get('race'),
            indictmentNumber: params.get('indictmentNumber') || params.get('indictment-number'),
            crimeOrOffense: params.get('crimeOrOffense') || params.get('crime-or-offense'),
            revisedStatuteCode: params.get('revisedStatuteCode') || params.get('revised-statute-code'),
            convictionCounty: params.get('convictionCounty') || params.get('conviction-county'),
            crimeDate: params.get('crimeDate') || params.get('crime-date'),
            convictionDate: params.get('convictionDate') || params.get('conviction-date'),
            sentenceStartDate: params.get('sentenceStartDate') || params.get('sentence-start-date'),
            escapedDateStart: params.get('escapedDateStart') || params.get('escaped-date-start'),
            escapedDateEnd: params.get('escapedDateEnd') || params.get('escaped-date-end'),
            projectedReleaseDateStart: params.get('projectedReleaseDateStart') || params.get('projected-release-date-start'),
            projectedReleaseDateEnd: params.get('projectedReleaseDateEnd') || params.get('projected-release-date-end'),
            searchAlias: params.get('searchAlias') === 'true' || params.get('search-alias') === 'true'
          } as FilterOrSearchModel))
        ).subscribe({
          next: (queryParams: FilterOrSearchModel) => {
            this._queryParamsInitialized = true;
            this.searchModel = queryParams;
            this._advancedSearchStateService.setState(this.searchModel);
            // Rebuild chips on every query param change to keep filter counts accurate
            this._searchFilterChipService.rebuildAllChips(this.searchModel);
            if (this.isMobile) {
              this.calculateOffscreenSearchChips();
            }
            this.loading = true;
            this.clearSearchResults();
            this.loadInmates();
          }
        })
    );

    this._subscriptions.push(this._breakpointObserver
      .observe(['(max-width: 768px)'])
      .subscribe(result => {
        const preChangeIsMobile = this.isMobile;
        this.isMobile = result.matches;

        if (this.isMobile && this.isMobile != preChangeIsMobile) {
          this.calculateOffscreenSearchChips();
        }
      }));
  }

  protected isSearchDisabled(): boolean {
    return isSearchDisabled(this.searchModel?.nameOrId);
  }

  async onSearch(allowEmpty = false) {
    if (!allowEmpty && this.isSearchDisabled()) return;

    this.loading = true;
    this.clearSearchResults();

    // Clear any existing nameOrId chip first
    this._searchFilterChipService.removeChipAndReorder('nameOrId', null, false);

    // Trim leading and trailing whitespace from search model
    if (this.searchModel.nameOrId) {
      this.searchModel.nameOrId = this.searchModel.nameOrId.trim();
    }

    // Add the search term chip if there's a search term
    if (this.searchModel.nameOrId && this.searchModel.nameOrId !== '') {
      this._searchFilterChipService.addChip({
        key: 'nameOrId',
        value: this.searchModel.nameOrId
      });
    }

    this._searchFilterChipService.onSearchAndFilterClick();

    const navigated = await this._router.navigate([], {
      queryParams: this.searchModel,
      queryParamsHandling: 'merge'
    });

    // When the query params are unchanged (e.g. re-clicking Search after the
    // alias checkbox already updated the URL), the router performs a no-op
    // navigation and queryParamMap never emits, so loadInmates() would never
    // run and the loading spinner would hang. Trigger the load directly here.
    if (!navigated) {
      this.loadInmates();
    }
  }

  private clearSearchResults() {
    this.inmates = [];
    this.pagination = null;
    this.docPageNumber = 1;
  }

  clearSearch(): void {
    this.searchModel.nameOrId = null;
    this.onSearch(true);
  }

  goBack(): void {
    if (this.instanceAlias) {
      this._router.navigate(['/', this.instanceAlias]);
    } else {
      this._router.navigate(['../'], { relativeTo: this._activatedRoute });
    }
  }

  getAdvancedFilterCount(chips: { key: string, value: string }[] | null): number {
    if (!chips) return 0;
    return chips.filter(c => c.key !== 'nameOrId').length;
  }

  protected loadInmates(): void {
    if (!this.instanceConfig) {
      console.error('No instance config available');
      return;
    }

    const currentRouteAlias = this._activatedRoute.snapshot.paramMap.get('alias') ??
      this._activatedRoute.parent?.snapshot.paramMap.get('alias') ??
      null;

    if (
      currentRouteAlias &&
      this.instanceConfig.alias &&
      currentRouteAlias.toLowerCase() !== this.instanceConfig.alias.toLowerCase()
    ) {
      // Route has navigated to a different alias, but config hasn't updated yet.
      // Invalidate current results and defer search until matching config is loaded.
      if (this._searchSubscription) {
        this._searchSubscription.unsubscribe();
        this._searchSubscription = null;
      }
      this.inmates = [];
      this.pagination = null;
      this.docPageNumber = 1;
      this.loading = true;
      return;
    }

    const hasDocFilters = !!(
      this.searchModel.docNumber ||
      this.searchModel.pid ||
      this.searchModel.offenderType ||
      this.searchModel.supervisionStatus ||
      this.searchModel.locationType ||
      this.searchModel.facility ||
      this.searchModel.countyOfSupervision ||
      this.searchModel.indictmentNumber ||
      this.searchModel.crimeOrOffense ||
      this.searchModel.revisedStatuteCode ||
      this.searchModel.convictionCounty ||
      this.searchModel.crimeDate ||
      this.searchModel.convictionDate ||
      this.searchModel.sentenceStartDate ||
      this.searchModel.escapedDateStart ||
      this.searchModel.escapedDateEnd ||
      this.searchModel.projectedReleaseDateStart ||
      this.searchModel.projectedReleaseDateEnd
    );

    const queryRecordType = this._activatedRoute.snapshot.queryParamMap.get('record-type') ||
      this._activatedRoute.snapshot.queryParamMap.get('recordType');

    const recordType: 'doc' | 'non-doc' = (queryRecordType as 'doc' | 'non-doc') ||
      (this.instanceConfig.branding?.searchResultType as 'doc' | 'non-doc') ||
      (hasDocFilters ? 'doc' : 'non-doc');

    this.isDocSearch = recordType === 'doc';

    const nextPageNumber = this.pagination?.pageNumber ? this.pagination.pageNumber + 1 : 1;
    const { sortBy, sort } = this.getSortParams();

    const targetInstanceId = this.instanceConfig.instanceId;
    this.loading = true;
    if (this._searchSubscription) {
      this._searchSubscription.unsubscribe();
      this._searchSubscription = null;
    }

    this._searchSubscription = this._inmateService.searchInmates(
      this.instanceConfig.instanceId,
      this.searchModel.nameOrId ?? undefined,
      this.searchModel.arrestingAgencies ?? undefined,
      this.searchModel.minAge ?? undefined,
      this.searchModel.maxAge ?? undefined,
      this.searchModel.minArrestDate ?? undefined,
      this.searchModel.maxArrestDate ?? undefined,
      10,
      nextPageNumber,
      sortBy,
      sort,
      recordType,
      this.searchModel.offenderType ?? undefined,
      this.searchModel.docNumber ?? undefined,
      this.searchModel.pid ?? undefined,
      this.searchModel.supervisionStatus ?? undefined,
      this.searchModel.locationType ?? undefined,
      this.searchModel.facility ?? undefined,
      this.searchModel.countyOfSupervision ?? undefined,
      this.searchModel.gender ?? undefined,
      this.searchModel.race ?? undefined,
      this.searchModel.indictmentNumber ?? undefined,
      this.searchModel.crimeOrOffense ?? undefined,
      this.searchModel.revisedStatuteCode ?? undefined,
      this.searchModel.convictionCounty ?? undefined,
      this.searchModel.crimeDate ?? undefined,
      this.searchModel.convictionDate ?? undefined,
      this.searchModel.sentenceStartDate ?? undefined,
      this.searchModel.escapedDateStart ?? undefined,
      this.searchModel.escapedDateEnd ?? undefined,
      this.searchModel.projectedReleaseDateStart ?? undefined,
      this.searchModel.projectedReleaseDateEnd ?? undefined,
      this.searchModel.searchAlias ? true : undefined
    )
      .subscribe({
        next: (n: SearchInmates200Response) => {
          if (this.instanceConfig?.instanceId !== targetInstanceId) {
            return;
          }
          this._lastRenderedInstanceId = targetInstanceId;
          this.inmates = (nextPageNumber === 1) ? (n.inmates || []) : this.inmates.concat(n.inmates || []);
          this.pagination = n.pagination;
          this.loading = false;
        },
        error: err => {
          if (this.instanceConfig?.instanceId !== targetInstanceId) {
            return;
          }
          // 429s are surfaced to the user via a toast (RateLimitInterceptor);
          if (err?.status === 429) {
            this.loading = false;
            return;
          }
          console.error(err);
          const state = getStateFromLoadError(err);
          this._errorPageService.showErrorPage(state);
        }
      });
    this._subscriptions.push(this._searchSubscription);

    this.updatedAt$ = this._instanceService.getInmateDataUpdated(this.instanceConfig.instanceId)
      .pipe(
        map(r => r.updatedAt),
        catchError((err) => {
          if (err.status !== 404) console.error(err);
          return 'Unknown';
        })
      );
  }

  /**
   * Maps the UI `sortBy` selection to the API `sort-by` field and `sort` direction.
   * Non-DOC options (arrest date / age) are unchanged. DOC options add name, PID
   * (numeric), DOC number (numeric) and location, all ascending. Sorting is applied server-side so it
   * stays correct across paginated "Load more" requests. The SDK types `sort-by`
   * as a closed union, but the value is forwarded verbatim as a query param, so we
   * widen the DOC fields with a cast.
   *
   * DOC records have no arrests, so when a DOC search has no DOC-specific sort
   * selected yet (initial load defaults `sortBy` to the non-DOC 'recentArrestDate'),
   * we fall back to sorting by inmate name to give a stable, meaningful order.
   */
  private getSortParams(): { sortBy: 'age' | 'arrestDate', sort: 'asc' | 'desc' } {
    const effectiveSortBy = this.isDocSearch && !this.docSortValues.has(this.searchModel.sortBy)
      ? 'inmateName'
      : this.searchModel.sortBy;

    // DOC options resolve straight from docSortOptions, so the dropdown list and
    // the API mapping cannot fall out of sync. Non-DOC options stay below.
    const docOption = this.docSortOptions.find(option => option.value === effectiveSortBy);
    if (docOption) {
      return { sortBy: docOption.apiSortBy, sort: docOption.apiSort };
    }

    switch (effectiveSortBy) {
      case 'youngestAge':
        return { sortBy: 'age', sort: 'asc' };
      case 'oldestAge':
        return { sortBy: 'age', sort: 'desc' };
      case 'oldestArrestDate':
        return { sortBy: 'arrestDate', sort: 'asc' };
      case 'recentArrestDate':
      default:
        return { sortBy: 'arrestDate', sort: 'desc' };
    }
  }

  onPersonClick(inmate: Inmate): void {
    const data = {
      inmate: inmate
    };

    const options: IDialogOptions = {
      preset: 'right-sheet',
      persistent: true
    };

    if (this.isDocSearch) {
      this._dialogService.open(DocInmateDetailsComponent, { options, data });
    } else {
      this._dialogService.open(InmateDetailsComponent, { options, data });
    }
  }

  scrollToTop(): void {
    this.searchBox.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  onSortOrFilterClick(): void {
    const options: IDialogOptions = {
      preset: 'right-sheet',
      label: 'Filter or sort',
      fullscreenThreshold: 768
    };

    const data = {
      title: 'Filter or search',
      description: 'Filter or search',
      instanceId: this.instanceConfig!.instanceId
    };

    const dialogRef = this._dialogService.open(PublicSearchSortFilterComponent, { options, data });
    dialogRef.afterClosed.subscribe(() => dialogRef.componentInstance.ngOnDestroy());
  }

  /**
   * The currently selected DOC sort option, shown in the inline dropdown.
   * DOC searches default `searchModel.sortBy` to the non-DOC 'recentArrestDate',
   * so surface 'inmateName' as the selected value when no DOC sort is chosen yet
   * (this mirrors the fallback in getSortParams()).
   */
  get docSortBy(): string {
    return this.docSortValues.has(this.searchModel.sortBy) ? this.searchModel.sortBy : 'inmateName';
  }

  async onDocSortChange(sortBy: string): Promise<void> {
    if (sortBy === this.searchModel.sortBy) return;

    await this._router.navigate([], {
      queryParams: { sortBy },
      queryParamsHandling: 'merge'
    });
  }

  onAdvancedSearch(): void {
    this._advancedSearchStateService.setState(this.searchModel);
    const options: IDialogOptions = {
      label: 'Advanced search filters',
      fullscreenThreshold: 768,
      persistent: true
    };

    const data = { instanceId: this.instanceConfig!.instanceId };
    const dialogRef = this._dialogService.open(AdvancedSearchModalComponent, { options, data });
    dialogRef.afterClosed.subscribe(async (result) => {
      if (result) {
        // Merge the new result with existing query params except for those being overwritten
        // Actually, the result contains all fields from the modal. We should merge them.
        const queryParams = Object.fromEntries(
          Object.entries(result).map(([k, v]) => [k, v === '' ? null : v])
        );

        await this._router.navigate([], {
          queryParams,
          queryParamsHandling: 'merge'
        });
      }
    });
  }

  onSearchChipRemoved(key: keyof FilterOrSearchModel, value: string): void {
    this._searchFilterChipService.removeChipAndReorder(key, value, true);

    switch (key) {
      case 'arrestingAgencies':
        this.searchModel.arrestingAgencies = this.searchModel.arrestingAgencies?.filter(aa => aa !== value) ?? [];
        break;
      case 'dateRange':
        this.searchModel.dateRange = null;
        this.searchModel.minArrestDate = null;
        break;
      case 'sortBy':
        break;
      default:
        this.searchModel[key] = null;
    }

    this.onSearch();
  }

  protected onRemoveChip(event: Event, chip: { key: keyof FilterOrSearchModel; value: string }): void {
    if (event instanceof KeyboardEvent && event.key === ' ') {
      event.preventDefault();
    }
    this.onSearchChipRemoved(chip.key, chip.value);
  }

  getLastNDaysLabel(value: string): string {
    const numericValue = Number(value);
    const entry = Object.entries(LastNDays).find(([_, v]) => v === numericValue);
    return entry ? entry[0] : value;
  }

  // Chip keys whose values are dates and should render as MM/DD/YYYY.
  private readonly dateChipKeys: ReadonlySet<string> = new Set([
    'minArrestDate', 'maxArrestDate', 'crimeDate', 'convictionDate', 'sentenceStartDate',
    'escapedDateStart', 'escapedDateEnd', 'projectedReleaseDateStart', 'projectedReleaseDateEnd'
  ]);

  isDateChip(key: string): boolean {
    return this.dateChipKeys.has(key);
  }

  getDateLabel(value: string): string {
    return DateUtils.formatDateMMDDYYYY(value);
  }

  getOptionLabel(key: string, value: string): string {
    return getDynamicOptionLabel(key, value, this._optionLabels);
  }

  calculateOffscreenSearchChips(): void {
    requestAnimationFrame(() => {
      const chipContainer = document.querySelector('.limit-chips');

      if (!chipContainer) {
        return;
      }

      const containerHeight = chipContainer.getBoundingClientRect().height;

      const firstChip = this.chipElements?.first?.nativeElement;

      const firstChipRowHeight = firstChip ? firstChip.offsetHeight : 0;

      let visibleChips = Math.floor(containerHeight / firstChipRowHeight);

      visibleChips = Math.min(visibleChips, this.chipElements?.length);

      this.hiddenChips = Math.max(0, this.chipElements?.length - visibleChips);
    });
  }

  toggleShowAllChips() {
    this.allChipsShowingMobile = !this.allChipsShowingMobile;
  }
}